import React, {useCallback, useRef} from 'react';

/** Custom theme based on snow default theme */
import '@/components/input/RichText/ql-theme.css';

import {useApi} from "@/uses/useApi";
import {useQuill} from "react-quilljs";
import objectUtil from "@/utils/objectUtil";

export const RichText = ({onChange, initialValue, ...props}) => {
    /** Key is the uploaded file url, this allows value to be compared directly to the input file object
     * because otherwise, the url key would be missing from input file object and deepEqual would be false
     * */
    const uploadedFilesRef = useRef({});

    const api = useApi();
    const editor = useRef(null);

    const handleImage = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = () => {
            const files = input.files;
            if(files.length !== 1){
                throw new Error('INVALID_UPLOAD');
            }

            const fileInfo = objectUtil.getFileInfo(files[0]);
            const existing = Object.keys(uploadedFilesRef.current).find((key) => {
                return objectUtil.deepEqual(uploadedFilesRef.current[key], fileInfo);
            });

            if(existing){
                const {index} = editor.current.getSelection();
                editor.current.insertEmbed(index, 'image', existing);
            } else {
                /** Get a presigned url to allow uploading to miniIO from front */
                api.FileService.getPresignedURL(files).then((res) => {
                    if (res?.data?.type === 'success') {
                        if(!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.presignedURL){
                            throw new Error('MISSING_PRESIGNED_URL');
                        }
                        if(!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.url){
                            throw new Error('MISSING_FILE_URL');
                        }
                        api.FileService.upload(res.data.value[0].presignedURL, files.item(0)).then(() => {
                            const {index} = editor.current.getSelection();
                            editor.current.insertEmbed(index, 'image', res?.data.value[0].url);
                            uploadedFilesRef.current = {
                                ...uploadedFilesRef.current,
                                [res?.data.value[0].url]: fileInfo
                            }
                        }).catch(console.error)
                    }
                });
            }
        };
        input.click();
    }, []);

    const modules = {
        toolbar: {
            handlers: {
                image: handleImage
            },
            container: [
                [{'header': [1, 2, false]}],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                ['link', 'image'],
                ['clean']
            ]
        }
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'indent',
        'link', 'image'
    ];

    const { quill, quillRef, ...quillProps } = useQuill({ theme: 'snow', modules, formats });
    editor.current = quillProps.editor;

    /** Initiate with initialValue */
    React.useEffect(() => {
        if (quill) {
            quill.clipboard.dangerouslyPasteHTML(initialValue);
        }
    }, [quill]);

    /** Listen to changes */
    React.useEffect(() => {
        if (quill) {
            quill.on('text-change', (delta, oldDelta, source) => {
                onChange(quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML);
            });
        }
    }, [quill]);

    return <div {...props}>
        <div
            ref={quillRef}
            onChange={onChange}
        />
    </div>;
}

export default RichText;
